import {
  inlayCoreProductSchema,
  ItemTypeEnum,
  productInlayCoreCharacteristicsSchema,
  productMaterialCharacteristicsSchema,
  productPreferencesSchema,
  ProductTypeEnum,
  type InlayCoreProduct,
} from "@jasper/client-models";
import {
  isTeethStepComplete,
  TeethSelectionEnum,
  type ProductWorkflowConfiguration,
  type WorkflowProduct,
} from "./ProductWorkflowConfiguration";
import { StepKeysEnum } from "../../steps/types";
import type { ToothStepProps } from "../../steps/TeethSteps/ToothStep";
import { defaultApplyTeethSelection } from "../../steps/TeethSteps/defaultApplyTeethSelection";
import {
  InlayCoreCrownStep,
  InlayCoreStep,
  PreferencesStep,
  ToothStep,
} from "./constants";

export type InlayCoreStepKey =
  | typeof StepKeysEnum.tooth
  | typeof StepKeysEnum.inlayCore
  | typeof StepKeysEnum.inlayCoreCrown
  | typeof StepKeysEnum.preferences;

// A new inlay-core comes with its crown: the crown step checkbox is checked by default.
const applyInlayCoreTeethSelection: ToothStepProps["applyTeethSelection"] = ({
  teeth,
  product,
  itemType,
}) =>
  product.items.length
    ? defaultApplyTeethSelection({ teeth, product, itemType })
    : {
        productId: product.id,
        teeth,
        items: [
          { tooth: teeth[0], type: itemType },
          { tooth: teeth[0], type: ItemTypeEnum.crown },
        ],
      };

const hasCrown = (product: WorkflowProduct) =>
  product.items?.some(item => item?.type === ItemTypeEnum.crown) ?? false;

export const InlayCoreWorkflowConfiguration: ProductWorkflowConfiguration<
  InlayCoreProduct,
  InlayCoreStepKey
> = {
  productType: ProductTypeEnum.inlayCore,
  itemType: ItemTypeEnum.inlayCore,
  steps: [
    new ToothStep({ applyTeethSelection: applyInlayCoreTeethSelection }),
    new InlayCoreStep(),
    new InlayCoreCrownStep(),
    new PreferencesStep({
      isSkipped: product => !hasCrown(product),
    }),
  ],
  teethSelection: TeethSelectionEnum.UNIQUE,
  isBulkSupported: true,

  isValidProduct(product) {
    return inlayCoreProductSchema.safeParse(product).success;
  },

  getCompletedSteps(product) {
    const completed: InlayCoreStepKey[] = [];

    if (
      isTeethStepComplete({
        teeth: product.teeth,
        teethSelection: this.teethSelection,
      })
    ) {
      completed.push(StepKeysEnum.tooth);
    }

    const inlayCoreItems = product.items?.filter(
      item => item?.type === ItemTypeEnum.inlayCore
    );
    const isInlayCoreStepComplete =
      productInlayCoreCharacteristicsSchema.safeParse({
        ...product,
        items: inlayCoreItems,
      }).success;

    if (inlayCoreItems?.length && isInlayCoreStepComplete) {
      completed.push(StepKeysEnum.inlayCore);
    }

    const crownItems = product.items?.filter(
      item => item?.type === ItemTypeEnum.crown
    );
    // No crown item means the crown was unchecked: the inlay-core is ordered alone.
    const isCrownStepComplete = crownItems?.length
      ? productMaterialCharacteristicsSchema.safeParse({
          ...product,
          items: crownItems,
        }).success
      : Boolean(inlayCoreItems?.length);

    if (isCrownStepComplete) {
      completed.push(StepKeysEnum.inlayCoreCrown);
    }

    if (productPreferencesSchema.safeParse(product).success) {
      completed.push(StepKeysEnum.preferences);
    }

    return completed;
  },
};
